<template>
  <model-form
    :model="membershipType"
  >
    <div
      class="column"
      :style="{ 'padding-bottom': membershipType.overrideCircuitTermOptions ? '' : '166px' }"
    >
      <div class="text-h5 q-mb-sm">
        Essentials
      </div>
      <div
        class="row q-col-gutter-sm items-center"
        style="max-height: 90px;"
      >
        <div
          class="col-1 column q-pt-none q-mb-md"
          style="height: 75px; width: 75px;"
        >
          <span
            class="text-caption"
            style="line-height: 11px;"
          >
            Icon
          </span>
          <image-uploader
            v-model="membershipType.iconImage"
            class="col"
            style="height: 64px; width: 64px;"
            :image-url="membershipType.iconImagePreviewUrl"
            :model="currentSite"
          />
        </div>
        <model-form-input
          field="name"
          class="col-6"
          label="Name"
          :rules="[val => val !== null || 'Field is required']"
        />
        <div class="col-5 row q-gutter-sm items-center justify-between">
          <model-form-checkbox
            field="default"
            class="col-auto q-pb-md"
            label="Default / Fallback"
            :disable="!canModifyDefault"
          >
            <q-tooltip>
              The default membership type when a user signs up before starting a membership, and the fallback effective membership if a paid membership lapses.
            </q-tooltip>
          </model-form-checkbox>
          <q-checkbox
            :model-value="membershipType.default ? true : membershipType.free"
            class="col-auto q-pb-md"
            label="Free"
            :disable="membershipType.default"
            bottom-slots
            @update:model-value="val => membershipType.free = val"
          >
            <q-tooltip v-if="membershipType.default">
              The default is required to be free
            </q-tooltip>
          </q-checkbox>
          <model-form-checkbox
            field="hide"
            class="col-auto q-pb-md"
            label="Hide"
          />
        </div>
      </div>
      <div class="row q-col-gutter-sm items-center">
        <model-form-checkbox
          field="availableOnline"
          class="col-3 q-pb-md"
          label="Available Online"
          :disable="membershipType.hide || membershipType.ageVerificationRequired"
        />
        <model-form-checkbox
          field="previewOnline"
          class="col-3 q-pb-md"
          label="Preview Online"
          :disable="membershipType.availableOnline || membershipType.default"
        >
          <q-tooltip>
            With this setting enabled, the membership will be visible online but not purchasable.
          </q-tooltip>
        </model-form-checkbox>
        <model-form-checkbox
          field="allowRenewalOnline"
          class="col-3 q-pb-md"
          label="Allow Renewal Online"
          :disable="membershipType.availableOnline || membershipType.default"
        >
          <q-tooltip>
            With this setting disabled, renewals for this membership type are only allowed at the register.
            When disabled this also prevents auto renewal.
          </q-tooltip>
        </model-form-checkbox>
        <div class="col-3" />
        <model-form-checkbox
          field="ageVerificationRequired"
          class="col-3 q-pb-md"
          label="Age Verification Required"
          :disable="membershipType.availableOnline || membershipType.default"
        >
          <q-tooltip>
            WARNING: When age verification is required, memberships can only be sold at the register.
          </q-tooltip>
        </model-form-checkbox>
      </div>
      <div class="row q-col-gutter-sm items-center">
        <model-form-select
          field="siteIds"
          class="col-10"
          :label="`${membershipType.excludeSites ? 'Exclude' : 'Include'} Sites`"
          :options="siteOptions"
          option-label="name"
          option-value="id"
          map-options
          emit-value
          multiple
          use-chips
          :disable="membershipType.default"
          clearable
        >
          <q-tooltip>
            Select sites where this membership type option will be shown or hidden.
            Membership benefits are still available at all sites unless specified otherwise.
            If no sites are selected, the membership type will be available at all sites.
          </q-tooltip>
        </model-form-select>
        <model-form-toggle
          field="excludeSites"
          class="col-2 q-pb-md"
          label="Exclude Sites"
          :disable="membershipType.default"
        />
      </div>
      <membership-user-type-table
        :membership-type="membershipType"
      />

      <div class="text-h5 q-mb-sm q-mt-xl">
        Marketing
      </div>
      <div class="row items-start q-mt-sm">
        <model-form-select
          field="descriptionType"
          class="col-6"
          label="Use Description As"
          :options="$mh.asOptions(MEMBERSHIP_TYPE_DESCRIPTION_TYPES.ALL)"
          :rules="[val => val !== null || 'Field is required']"
          map-options
          emit-value
        >
          <q-tooltip>
            The membership type selector auto-generated description currently only advertises unconditional ticket vouchers, discounts, and benefits.
            <br>Replace the description to advertise all membership benefits.
          </q-tooltip>
        </model-form-select>
        <div class="col-3" />
        <q-btn
          class="col-3"
          label="Preview"
          size="lg"
          outline
          @click="$refs.membershipTypeSelector.show(previewUser, true, false)"
        />
        <q-editor
          v-model="membershipType.description"
          class="col-12 custom-section"
          placeholder="Description"
          :dense="$q.screen.lt.md"
          :toolbar="[
            [
              {
                label: $q.lang.editor.align,
                icon: $q.iconSet.editor.align,
                fixedLabel: true,
                list: 'only-icons',
                options: ['left', 'center', 'right', 'justify']
              }
            ],
            ['bold', 'italic', 'strike', 'underline', 'subscript', 'superscript'],
            ['token', 'hr', 'link', 'custom_btn'],
            ['print', 'fullscreen'],
            [
              {
                label: $q.lang.editor.formatting,
                icon: $q.iconSet.editor.formatting,
                list: 'no-icons',
                options: [
                  'p',
                  'h1',
                  'h2',
                  'h3',
                  'h4',
                  'h5',
                  'h6',
                  'code'
                ]
              },
              {
                label: $q.lang.editor.fontSize,
                icon: $q.iconSet.editor.fontSize,
                fixedLabel: true,
                fixedIcon: true,
                list: 'no-icons',
                options: [
                  'size-1',
                  'size-2',
                  'size-3',
                  'size-4',
                  'size-5',
                  'size-6',
                  'size-7'
                ]
              },
              {
                label: $q.lang.editor.defaultFont,
                icon: $q.iconSet.editor.font,
                fixedIcon: true,
                list: 'no-icons',
                options: [
                  'default_font',
                  'arial',
                  'arial_black',
                  'comic_sans',
                  'courier_new',
                  'impact',
                  'lucida_grande',
                  'times_new_roman',
                  'verdana'
                ]
              },
              'removeFormat'
            ],
            ['quote', 'unordered', 'ordered', 'outdent', 'indent'],

            ['undo', 'redo'],
            ['viewsource']
          ]"
          :fonts="{
            arial: 'Arial',
            arial_black: 'Arial Black',
            comic_sans: 'Comic Sans MS',
            courier_new: 'Courier New',
            impact: 'Impact',
            lucida_grande: 'Lucida Grande',
            times_new_roman: 'Times New Roman',
            verdana: 'Verdana'
          }"
        />
      </div>

      <div class="text-h5 q-mb-sm q-mt-xl">
        Benefits
      </div>
      <membership-tier-table
        class="q-mt-sm q-mb-xs"
        :membership-type="membershipType"
      />
      <model-form-select
        field="conditionsTerm"
        class="q-mt-xs q-mb-lg"
        label="Tiers Conditions Term"
        :options="$mh.asOptions(MEMBERSHIP_TIER_CONDITIONS_TERMS.ALL)"
        :rules="[val => val !== null || 'Field is required']"
        map-options
        emit-value
      />
      <membership-point-table
        class="q-mt-sm q-mb-lg"
        :membership-type="membershipType"
      />
      <membership-voucher-table
        class="q-mt-sm q-mb-lg"
        :membership-type="membershipType"
      />
      <membership-discount-table
        class="q-mt-sm q-mb-lg"
        :membership-type="membershipType"
      />
      <div
        class="row q-gutter-x-md items-center q-mb-sm q-mt-xl "
      >
        <div class="col-auto text-h5 q-my-none">
          Terms
        </div>
        <model-form-toggle
          field="overrideCircuitTermOptions"
          class="col-auto"
          label="Override Circuit"
        />
      </div>
      <div
        class="row q-col-gutter-sm items-start q-mt-sm"
      >
        <model-form-checkbox
          field="allowMonthlyMembership"
          :disable="!membershipType.overrideCircuitTermOptions"
          class="col"
          label="Allow Monthly Membership"
        />
        <model-form-checkbox
          field="allowQuarterlyMembership"
          :disable="!membershipType.overrideCircuitTermOptions"
          class="col"
          label="Allow Quarterly Membership"
        />
        <model-form-checkbox
          field="allowBiannualMembership"
          :disable="!membershipType.overrideCircuitTermOptions"
          class="col"
          label="Allow Biannual Membership"
        />
        <model-form-checkbox
          field="allowAnnualMembership"
          :disable="!membershipType.overrideCircuitTermOptions"
          class="col"
          label="Allow Annual Membership"
        />
      </div>
      <div
        class="row q-col-gutter-sm justify-end items-start q-mt-md"
      >
        <model-form-input
          field="quarterlyDiscountPercentThousandths"
          :decode="val => val / 1000"
          :encode="val => val * 1000"
          :disable="!membershipType.overrideCircuitTermOptions || !membershipType.allowQuarterlyMembership"
          class="col-3"
          label="Quarterly Discount Percent"
          suffix="%"
          type="number"
          min="0"
          max="100"
          step="5"
          :rules="[val => val !== null || 'Field is required']"
        />
        <model-form-input
          field="biannualDiscountPercentThousandths"
          :decode="val => val / 1000"
          :encode="val => val * 1000"
          :disable="!membershipType.overrideCircuitTermOptions || !membershipType.allowBiannualMembership"
          class="col-3"
          label="Biannual Discount Percent"
          suffix="%"
          type="number"
          min="0"
          max="100"
          step="5"
          :rules="[val => val !== null || 'Field is required']"
        />
        <model-form-input
          field="annualDiscountPercentThousandths"
          :decode="val => val / 1000"
          :encode="val => val * 1000"
          :disable="!membershipType.overrideCircuitTermOptions || !membershipType.allowAnnualMembership"
          class="col-3"
          label="Annual Discount Percent"
          suffix="%"
          type="number"
          min="0"
          max="100"
          step="5"
          :rules="[val => val !== null || 'Field is required']"
        />
      </div>
    </div>
    <membership-type-selector
      ref="membershipTypeSelector"
      :preview-membership-type="membershipType"
    />
  </model-form>
</template>

<script>

import { MEMBERSHIP_TIER_CONDITIONS_TERMS, MEMBERSHIP_TYPE_DESCRIPTION_TYPES } from 'src/models/membership-type/MembershipType'
import User from 'src/models/user/User'
import ImageUploader from 'src/modules/admin/components/ImageUploader.vue'
import ModelForm from 'src/modules/admin/components/ModelForm/ModelForm'
import MembershipDiscountTable from 'src/modules/admin/membership-type/MembershipDiscountTable'
import MembershipPointTable from 'src/modules/admin/membership-type/MembershipPointTable'
import MembershipTierTable from 'src/modules/admin/membership-type/MembershipTierTable'
import MembershipUserTypeTable from 'src/modules/admin/membership-type/MembershipUserTypeTable'
import MembershipVoucherTable from 'src/modules/admin/membership-type/MembershipVoucherTable'
import MembershipTypeSelector from 'src/modules/register/membership/MembershipTypeSelector'

const previewUser = new User({}, {})

export default {
  name: 'MembershipTypeForm',
  components: {
    ModelForm,
    MembershipDiscountTable,
    MembershipPointTable,
    MembershipTierTable,
    MembershipUserTypeTable,
    MembershipVoucherTable,
    MembershipTypeSelector,
    ImageUploader
  },
  context: ['currentUser'],
  configuration: ['currentSite', 'discounts', 'membershipTypes', 'sites', 'voucherTypes'],
  models: {
    membershipType: {
      membershipUserTypes: {
        userClass: 'default',
        monthlyRate: 'default',
        additionalMonthlyRate: 'default'
      },
      membershipTiers: 'default',
      membershipPoints: {
        discounts: {
          fields: ['id', 'name']
        },
        membershipTiers: {
          fields: ['id', 'name']
        },
        membershipTierIds: 'default',
        siteItems: {
          fields: ['id', 'name']
        },
        siteItemIds: 'default',
        itemClasses: {
          fields: ['id', 'name']
        },
        siteItemPackages: {
          fields: ['id', 'name']
        },
        siteItemPackageIds: 'default',
        siteMovies: {
          fields: ['id', 'name']
        },
        siteMovieIds: 'default',
        showingBadges: {
          fields: ['id', 'name']
        },
        sites: {
          fields: ['id', 'name']
        },
        titleClasses: {
          fields: ['id', 'name']
        },
        voucherTypes: {
          fields: ['id', 'name']
        }
      },
      membershipVouchers: {
        voucherType: 'default',
        sites: {
          fields: ['id', 'name']
        },
        membershipTierIds: 'default'
      },
      membershipDiscounts: {
        discount: 'default',
        sites: {
          fields: ['id', 'name']
        },
        membershipTierIds: 'default'
      },
      sites: {
        fields: ['id', 'name']
      },
      unlimitedTicketVoucherType: 'default',
      monthlyTicketVoucherType: 'default',
      discounts: 'default',
      editing: true
    }
  },
  data () {
    return {
      pagination: {
        page: 1,
        rowsPerPage: 0
      }
    }
  },
  computed: {
    siteOptions () {
      return _.filter(this.sites.data, { isLibrary: false, active: true })
    },
    canModifyDefault () {
      return _.some(this.membershipTypes.data, membershipType => membershipType.default && membershipType.id !== this.membershipType.id)
    }
  },
  created () {
    _.extend(this, { MEMBERSHIP_TIER_CONDITIONS_TERMS, MEMBERSHIP_TYPE_DESCRIPTION_TYPES, previewUser })
  },
  unmounted () {
    this.previewUser.destroy()
  }
}

</script>
